/**
 * SiteMapPanel — dependency-free dark map for a customer site.
 *
 * Uses CartoDB "Dark Matter" raster tiles (open-licence, no API key) fetched
 * directly via the standard slippy-map tile URL. A 3 × 3 tile grid is drawn
 * with the site at the centre of the middle tile; additional markers
 * (samples, secondary sites) are overlaid by pixel offset.
 *
 * If the site has no stored lat/lng but does have a postcode, we run a one-shot
 * Nominatim reverse geocode and cache the result in localStorage so subsequent
 * loads are instant.
 */
import { useEffect, useMemo, useState } from 'react';
import { MapPin, Plus, Minus, ExternalLink, Loader2, Crosshair, AlertCircle } from 'lucide-react';

export interface MapMarker {
  lat: number;
  lng: number;
  label: string;
  type?: 'site' | 'sample' | 'equipment';
  /** Optional tooltip / extra info */
  detail?: string;
}

export interface SiteMapPanelProps {
  siteName: string;
  postcode?: string | null;
  /** If you already know coordinates, pass them and we skip geocoding. */
  lat?: number | null;
  lng?: number | null;
  /** Country code for geocoding bias (e.g. 'gb'). */
  country?: string | null;
  /** Extra markers (samples, equipment locations, neighbouring sites). */
  markers?: MapMarker[];
  /** Initial zoom level (OSM 0–19). Default 14 (district scale). */
  zoom?: number;
  /** Override panel height (px). */
  height?: number;
}

const TILE_PX = 256;
const GRID = 3;                  // 3 × 3 tiles
const VIEW = TILE_PX * GRID;     // 768 px viewport

// CartoDB Dark Matter tile URL pattern (subdomains a/b/c/d).
const TILE_URL = (z: number, x: number, y: number, sub: string) =>
  `https://${sub}.basemaps.cartocdn.com/dark_all/${z}/${x}/${y}.png`;

const LS_GEOCODE = 'rb_geocode_cache_v1';

interface GeocodeCache { [postcode: string]: { lat: number; lng: number; at: string } }

function readGeocodeCache(): GeocodeCache {
  try { return JSON.parse(localStorage.getItem(LS_GEOCODE) || '{}'); } catch { return {}; }
}
function writeGeocodeCache(c: GeocodeCache) {
  try { localStorage.setItem(LS_GEOCODE, JSON.stringify(c)); } catch { /* ignore */ }
}

/** Web Mercator helpers. Standard OSM slippy-map formulas. */
function lon2tileX(lon: number, z: number) { return ((lon + 180) / 360) * Math.pow(2, z); }
function lat2tileY(lat: number, z: number) {
  const rad = (lat * Math.PI) / 180;
  return (1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2 * Math.pow(2, z);
}

/** Convert a (lat,lng) to its pixel offset relative to the centre of the 3x3 grid,
 *  given the centre site's coordinates and zoom. */
function offsetPx(siteLat: number, siteLng: number, mLat: number, mLng: number, z: number) {
  const xCenter = lon2tileX(siteLng, z) * TILE_PX;
  const yCenter = lat2tileY(siteLat, z) * TILE_PX;
  const xPx     = lon2tileX(mLng, z) * TILE_PX;
  const yPx     = lat2tileY(mLat, z) * TILE_PX;
  return { dx: xPx - xCenter, dy: yPx - yCenter };
}

/** Try to geocode a UK postcode (or any address-ish string) via Nominatim. */
async function geocode(query: string, country?: string | null): Promise<{ lat: number; lng: number } | null> {
  try {
    const params = new URLSearchParams({
      format: 'json',
      q: query,
      limit: '1',
      addressdetails: '0',
    });
    if (country) params.set('countrycodes', country.toLowerCase());
    const res = await fetch(`https://nominatim.openstreetmap.org/search?${params.toString()}`, {
      headers: { 'Accept': 'application/json' },
    });
    if (!res.ok) return null;
    const arr = await res.json();
    if (!Array.isArray(arr) || !arr[0]) return null;
    return { lat: parseFloat(arr[0].lat), lng: parseFloat(arr[0].lon) };
  } catch { return null; }
}

export default function SiteMapPanel(props: SiteMapPanelProps) {
  const { siteName, postcode, country, markers = [], height = 380 } = props;
  const [zoom, setZoom] = useState<number>(props.zoom ?? 14);
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(
    props.lat != null && props.lng != null ? { lat: props.lat, lng: props.lng } : null
  );
  const [geocoding, setGeocoding] = useState(false);
  const [geocodeError, setGeocodeError] = useState<string | null>(null);

  // Lazy geocode of postcode if no explicit lat/lng
  useEffect(() => {
    if (coords) return;
    const query = (postcode ?? '').trim();
    if (!query) return;
    const cache = readGeocodeCache();
    const key = `${query.toUpperCase()}|${(country ?? '').toUpperCase()}`;
    if (cache[key]) {
      setCoords(cache[key]);
      return;
    }
    setGeocoding(true);
    setGeocodeError(null);
    geocode(query, country)
      .then(r => {
        if (r) {
          const next = { ...cache, [key]: { ...r, at: new Date().toISOString() } };
          writeGeocodeCache(next);
          setCoords(r);
        } else {
          setGeocodeError('Could not locate this postcode on the map. Add coordinates manually if you have them.');
        }
      })
      .catch(e => setGeocodeError(e?.message ?? 'Geocoding failed'))
      .finally(() => setGeocoding(false));
  }, [postcode, country, coords]);

  // 3 × 3 tile grid centred on the site
  const tiles = useMemo(() => {
    if (!coords) return null;
    const cx = Math.floor(lon2tileX(coords.lng, zoom));
    const cy = Math.floor(lat2tileY(coords.lat, zoom));
    const arr: Array<{ x: number; y: number; sub: string; offsetX: number; offsetY: number }> = [];
    const subs = ['a', 'b', 'c', 'd'];
    for (let row = -1; row <= 1; row++) {
      for (let col = -1; col <= 1; col++) {
        const tx = cx + col;
        const ty = cy + row;
        arr.push({
          x: tx, y: ty,
          sub: subs[(Math.abs(tx + ty)) % subs.length],
          offsetX: (col + 1) * TILE_PX,
          offsetY: (row + 1) * TILE_PX,
        });
      }
    }
    return { cx, cy, tiles: arr };
  }, [coords, zoom]);

  // Pixel offset of the centre tile origin so the site pin sits exactly on its coordinates
  const centreOffset = useMemo(() => {
    if (!coords) return { dx: 0, dy: 0 };
    const xPx = lon2tileX(coords.lng, zoom) * TILE_PX;
    const yPx = lat2tileY(coords.lat, zoom) * TILE_PX;
    const xTile = Math.floor(lon2tileX(coords.lng, zoom));
    const yTile = Math.floor(lat2tileY(coords.lat, zoom));
    return { dx: xPx - xTile * TILE_PX, dy: yPx - yTile * TILE_PX };
  }, [coords, zoom]);

  const externalLink = coords
    ? `https://www.openstreetmap.org/?mlat=${coords.lat}&mlon=${coords.lng}#map=${zoom}/${coords.lat}/${coords.lng}`
    : null;

  const containerStyle: React.CSSProperties = {
    height,
    background: '#070b14',
    border: '1px solid rgba(203,164,46,0.18)',
    borderRadius: 12,
    boxShadow: '0 8px 24px rgba(0,0,0,0.45), inset 0 0 0 1px rgba(255,255,255,0.04)',
    overflow: 'hidden',
    position: 'relative',
  };

  if (!coords) {
    return (
      <div style={containerStyle} className="flex items-center justify-center">
        {geocoding ? (
          <div className="text-slate-400 flex items-center gap-2 text-sm">
            <Loader2 className="w-4 h-4 animate-spin text-cyan-400" />
            Locating <span className="text-cyan-300 font-mono">{postcode}</span>…
          </div>
        ) : geocodeError ? (
          <div className="text-center px-6">
            <AlertCircle className="w-8 h-8 text-rose-400 mx-auto mb-2" />
            <p className="text-sm text-rose-300 font-bold">{siteName}</p>
            <p className="text-xs text-slate-400 max-w-xs mx-auto mt-1">{geocodeError}</p>
          </div>
        ) : (
          <div className="text-center px-6">
            <MapPin className="w-8 h-8 text-slate-600 mx-auto mb-2" />
            <p className="text-sm text-slate-400">No location set</p>
            <p className="text-xs text-slate-600 mt-1">Add a postcode to this site to display the map.</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {/* Tile grid — positioned so the site coordinate sits at the panel centre */}
      <div
        style={{
          position: 'absolute',
          width: VIEW, height: VIEW,
          left: '50%', top: '50%',
          transform: `translate(${-TILE_PX - centreOffset.dx}px, ${-TILE_PX - centreOffset.dy}px)`,
          imageRendering: 'pixelated',
        }}
      >
        {tiles?.tiles.map((t, i) => (
          <img
            key={i}
            src={TILE_URL(zoom, t.x, t.y, t.sub)}
            width={TILE_PX} height={TILE_PX}
            alt=""
            style={{ position: 'absolute', left: t.offsetX, top: t.offsetY, opacity: 0.95 }}
            onError={e => { (e.target as HTMLImageElement).style.opacity = '0'; }}
          />
        ))}
        {/* Sample / equipment markers (computed by lat/lng offset from the site) */}
        {markers.map((m, i) => {
          const o = offsetPx(coords.lat, coords.lng, m.lat, m.lng, zoom);
          const cx = TILE_PX + centreOffset.dx + o.dx;
          const cy = TILE_PX + centreOffset.dy + o.dy;
          if (cx < 0 || cx > VIEW || cy < 0 || cy > VIEW) return null;
          const colour = m.type === 'sample' ? '#06b6d4' : m.type === 'equipment' ? '#a855f7' : '#cda42e';
          return (
            <div key={i}
              title={`${m.label}${m.detail ? ` — ${m.detail}` : ''}`}
              style={{
                position: 'absolute', left: cx - 6, top: cy - 6,
                width: 12, height: 12, borderRadius: '50%',
                background: colour,
                boxShadow: `0 0 0 2px rgba(15,23,42,0.85), 0 0 12px ${colour}`,
                pointerEvents: 'auto', cursor: 'help',
              }} />
          );
        })}
      </div>

      {/* Centre pin (site itself) — drawn over the tile layer, always at view centre */}
      <div style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -100%)', pointerEvents: 'none' }}>
        <svg width="40" height="50" viewBox="0 0 40 50">
          <defs>
            <radialGradient id="rbPinGlow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#fbbf24" stopOpacity="0.55" />
              <stop offset="100%" stopColor="#fbbf24" stopOpacity="0" />
            </radialGradient>
          </defs>
          <circle cx="20" cy="20" r="18" fill="url(#rbPinGlow)">
            <animate attributeName="r" values="14;22;14" dur="2.4s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.9;0.3;0.9" dur="2.4s" repeatCount="indefinite" />
          </circle>
          <path d="M 20 5 C 12 5 7 11 7 18 C 7 26 20 44 20 44 C 20 44 33 26 33 18 C 33 11 28 5 20 5 Z"
                fill="#cda42e" stroke="#0f172a" strokeWidth="1.8" />
          <circle cx="20" cy="18" r="5" fill="#0f172a" />
        </svg>
      </div>

      {/* Top-left site label */}
      <div style={{ position: 'absolute', top: 10, left: 10, maxWidth: '60%' }}>
        <div className="px-3 py-1.5 rounded-md text-xs font-bold text-amber-200 backdrop-blur-sm flex items-center gap-2"
             style={{ background: 'rgba(15,23,42,0.85)', border: '1px solid rgba(205,164,46,0.45)', boxShadow: '0 2px 8px rgba(0,0,0,0.5)' }}>
          <MapPin className="w-3.5 h-3.5" />
          {siteName}
        </div>
        <div className="px-3 py-1 mt-1 rounded text-[10px] text-slate-300 font-mono"
             style={{ background: 'rgba(15,23,42,0.8)', border: '1px solid rgba(51,65,85,0.6)' }}>
          {coords.lat.toFixed(5)}, {coords.lng.toFixed(5)}{postcode ? ` · ${postcode}` : ''}
        </div>
      </div>

      {/* Top-right zoom controls */}
      <div style={{ position: 'absolute', top: 10, right: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <button onClick={() => setZoom(z => Math.min(18, z + 1))}
          className="w-8 h-8 rounded-md flex items-center justify-center text-amber-200 hover:bg-amber-500/20"
          style={{ background: 'rgba(15,23,42,0.85)', border: '1px solid rgba(205,164,46,0.4)' }}
          title={`Zoom in (current ${zoom})`}>
          <Plus className="w-4 h-4" />
        </button>
        <button onClick={() => setZoom(z => Math.max(3, z - 1))}
          className="w-8 h-8 rounded-md flex items-center justify-center text-amber-200 hover:bg-amber-500/20"
          style={{ background: 'rgba(15,23,42,0.85)', border: '1px solid rgba(205,164,46,0.4)' }}
          title="Zoom out">
          <Minus className="w-4 h-4" />
        </button>
        <button onClick={() => setZoom(14)}
          className="w-8 h-8 rounded-md flex items-center justify-center text-slate-200 hover:bg-slate-500/20"
          style={{ background: 'rgba(15,23,42,0.85)', border: '1px solid rgba(71,85,105,0.5)' }}
          title="Reset zoom">
          <Crosshair className="w-4 h-4" />
        </button>
      </div>

      {/* Bottom-right attribution + external link */}
      {externalLink && (
        <div style={{ position: 'absolute', bottom: 8, right: 10 }}>
          <a href={externalLink} target="_blank" rel="noopener noreferrer"
             className="text-[10px] text-amber-300 hover:text-amber-200 flex items-center gap-1 px-2 py-1 rounded"
             style={{ background: 'rgba(15,23,42,0.85)', border: '1px solid rgba(205,164,46,0.3)' }}>
            <ExternalLink className="w-3 h-3" /> OSM
          </a>
        </div>
      )}

      {/* Bottom-left legend (only if markers exist) */}
      {markers.length > 0 && (
        <div style={{ position: 'absolute', bottom: 8, left: 10 }}>
          <div className="flex items-center gap-2 px-2 py-1 rounded text-[10px] text-slate-300"
               style={{ background: 'rgba(15,23,42,0.85)', border: '1px solid rgba(51,65,85,0.6)' }}>
            <span className="w-2 h-2 rounded-full bg-amber-400 shadow-[0_0_6px_rgba(251,191,36,0.9)]" /> Site
            <span className="w-2 h-2 rounded-full bg-cyan-400 ml-2 shadow-[0_0_6px_rgba(34,211,238,0.9)]" /> Sample
            <span className="w-2 h-2 rounded-full bg-purple-400 ml-2 shadow-[0_0_6px_rgba(168,85,247,0.9)]" /> Equipment
            <span className="ml-2 text-slate-500">· {markers.length} marker{markers.length === 1 ? '' : 's'}</span>
          </div>
        </div>
      )}

      {/* Attribution micro-line */}
      <div style={{ position: 'absolute', bottom: 2, right: 60 }}>
        <p className="text-[8px] text-slate-600 font-mono">© OpenStreetMap · CartoDB Dark</p>
      </div>
    </div>
  );
}
